import { useContext, useEffect, useState, useMemo } from "react";

//Components
import {
  Modal,
  TextField,
  Button,
  Popper,
  Paper,
  Typography,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import { MdHelpOutline } from "react-icons/md";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_ADDRESS } from "../Queries/GetAddress";
import { ADDRESS_AUTO_SUGGEST } from "../Queries/AddressAutoSuggest";
import { ADD_OR_UPDATE_ADDRESS } from "../Mutations/AddOrUpdateAddress";

//Styles
import { useStyles } from "../Styles/styles";
import {
  handleUuid,
  parseAddressName,
  addressAlertColor,
  STATE_TO_ABBREVIATION,
} from "../utils/utils";
import { UserContext } from "../Contexts/UserContext";
import clsx from "clsx";

//Utils
import { shouldBeLocked } from "../utils/Roles";
import { VT_CITY_ZIP_CODES, NH_CITY_ZIP_CODES } from "../utils/CityZipCodes";
import { SetupContext } from "../Contexts/SetupContext";
import { useToggle } from "../CustomHooks/useToggle";
import { throttleFunction } from "../utils/throttleFunction";

const STATE_CODE = import.meta.env.VITE_STATE_CODE?.toUpperCase() || "VT";
const CITY_ZIP_CODES =
  STATE_CODE === "NH" ? NH_CITY_ZIP_CODES : VT_CITY_ZIP_CODES;
const CITY_ARRAY = Object.keys(CITY_ZIP_CODES);
const AddressInputAutoSuggest = ({
  title,
  address,
  id,
  setAddress,
  updateType,
  textfieldClass,
  disabled = undefined,
  userRoles,
  pauseLocationPolling = () => alert("Add pauseLocationPolling"),
  resumeLocationPolling = () => alert("Add resumeLocationPolling"),
  autoFocus = false,
  customClass = "",
  ...props
}) => {
  const { responseErrorHandler, user, onlineState } = useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const { setupLists } = setup || {};
  const { states } = setupLists || [];
  const statesStrings = states?.map(s => s.name) || [];
  const [anchorEl, setAnchorEl] = useState(null);
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const addressName = useMemo(
    () => (address?.addressName ? address?.addressName : address?.name),
    [address]
  );
  const classes = useStyles();
  const [
    addressAutoSuggest,
    {
      loading: addressAutoSuggestLoading,
      error: addressAutoSuggestError,
      data: addressAutoSuggestData,
    },
  ] = useLazyQuery(ADDRESS_AUTO_SUGGEST, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onCompleted: data => data && responseErrorHandler(data),
  });

  const [showPopper, togglePopper] = useToggle(false);

  const FONT_SIZE = 8;
  const DEFAULT_INPUT_WIDTH = 200;

  const [inputWidth, setInputWidth] = useState(DEFAULT_INPUT_WIDTH);

  useEffect(() => {
    if (addressName?.length * FONT_SIZE > DEFAULT_INPUT_WIDTH) {
      setInputWidth((addressName?.length + 1) * FONT_SIZE);
    } else {
      setInputWidth(DEFAULT_INPUT_WIDTH);
    }
  }, [addressName]);

  const [createAddress] = useMutation(ADD_OR_UPDATE_ADDRESS, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const address = data.addOrUpdateAddress.address;
        setAddress(address, false);
        setLocalAddress(address);
      }
    },
  });

  const throttledCreateAddress = throttleFunction(createAddress);

  const [getAddress] = useLazyQuery(GET_ADDRESS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        handleAddressChange(data.getAddress);
      }
    },
  });

  const filter = createFilterOptions();

  const [addressOptions, setAddressOptions] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [previousSearchValue, setPreviousSearchValue] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [addAddressModal, setAddAddressModal] = useState(false);

  const [newAddress, setNewAddress] = useState({
    streetNumber: "",
    streetName: "",
    unit: "",
    city: "",
    state: "",
    zip: "",
  });

  const handlePopperClick = e => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
    togglePopper();
  };

  const handleKeyBlur = event => {
    if (event.keyCode === 27 || event.keyCode === 13) {
      setShowModal(false);
    }
  };

  const createNewAddress = () => {
    let addressUuid = handleUuid();
    let input = {
      id: addressUuid,
      unit: newAddress.unit,
      streetNumber: newAddress.streetNumber,
      streetName: newAddress.streetName,
      city: newAddress.city,
      state: newAddress.state,
      zip: newAddress.zip,
      // name: newAddress.streetNumber + " " + newAddress.streetName + " " + newAddress.unit +", "+newAddress.city+", "+newAddress.state+", "+newAddress.zip
    };
    input.name = parseAddressName(input);
    let mutationInput = {
      originatorId: user?.id,
      id: addressUuid,
      unit: newAddress.unit,
      streetNumber: newAddress.streetNumber,
      streetName: newAddress.streetName,
      city: newAddress.city,
      state: newAddress.state,
      zip: newAddress.zip,
    };
    throttledCreateAddress({
      variables: { input: mutationInput },
      context: { serializationKey: "incidentQueue" },
    });
    setAddress(input);
    setLocalAddress(input);
    setAddAddressModal(false);
    !!resumeLocationPolling && resumeLocationPolling();
  };

  const [localAddress, setLocalAddress] = useState(address);

  const handleAddressChange = address => {
    setAddress(address);
    setLocalAddress(address);
  };

  const parseInputToAddress = str => {
    setNewAddress(prevState => {
      return {
        ...prevState,
        streetName: str.trim() || "",
      };
    });
  };

  const handleNewAddressChange = event => {
    const name = event.target.name;
    const value = event.target.value;
    if (name === "street") {
      let streetArr = value.split(" ");
      let streetNumber = streetArr[0];
      streetArr.splice(0, 1);
      let streetName = streetArr.join(" ");
      setNewAddress(prevState => {
        return {
          ...prevState,
          streetNumber: streetNumber || "",
          streetName: streetName || "",
        };
      });
    } else {
      setNewAddress(prevState => {
        return {
          ...prevState,
          [name]: value,
        };
      });
    }
  };
  useEffect(() => {
    if (searchValue && searchValue.length > 2 && onlineState) {
      //If the new search value contains the previous search value that returned no results don't query again
      if (!searchValue.includes(previousSearchValue)) {
        addressAutoSuggest({ variables: { input: searchValue, count: 20 } });
      }
    }
  }, [searchValue, previousSearchValue, addressAutoSuggest, onlineState]);

  useEffect(() => {
    if (
      addressAutoSuggestData &&
      !addressAutoSuggestLoading &&
      !addressAutoSuggestError
    ) {
      const suggestedAddresses = Array.isArray(
        addressAutoSuggestData?.addressAutoSuggest
      )
        ? addressAutoSuggestData.addressAutoSuggest
        : [];

      setAddressOptions(suggestedAddresses);
      //When the search term returns no results store the search value
      if (suggestedAddresses.length === 0) {
        setPreviousSearchValue(searchValue);
      }
    }
  }, [
    addressAutoSuggestData,
    addressAutoSuggestLoading,
    addressAutoSuggestError,
    searchValue,
  ]);

  const handleBlur = () => {
    !!resumeLocationPolling && resumeLocationPolling();
    if (searchValue === "") {
      // if (updateType === 'person') {
      //   updatePerson({ variables: { input: { personId: id, homeAddressId: "" } } })
      // }
      // if (updateType === 'incident') {
      //   updateCallInfo({ variables: { input: { incidentId: id, addressId: "", originatorId: user?.id } } })
      // }
      setAddress(prevState => {
        return {
          ...prevState,
          city: "",
          lat: "",
          lng: "",
          addressName: "",
          state: "",
          streetName: "",
          streetNumber: "",
          unit: "",
          zip: "",
        };
      });
      setLocalAddress(prevState => {
        return {
          ...prevState,
          city: "",
          lat: "",
          lng: "",
          addressName: "",
          state: "",
          streetName: "",
          streetNumber: "",
          unit: "",
          zip: "",
        };
      });
    }
    setShowModal(false);
  };
  return (
    <>
      <span style={{ display: "flex", flexDirection: "row" }}>
        {
          !showModal ? (
            //  <FormControl data-cy="narrative-full-width-form-control" fullWidth className={classes.formControl}>

            // eslint-disable-next-line jsx-a11y/no-autofocus -- Preserve initial focus when requested by the parent.
            <TextField autoFocus={autoFocus}
              disabled={memoizedLocked}
              InputLabelProps={{ shrink: true }}
              data-cy={title + "-textfield-" + id}
              id={title + id + "-id"}
              label={title}
              value={addressName ? addressName : ""}
              // width='100%'
              style={{ flexGrow: 1 }}
              className={clsx(customClass)}
              //Differentiate between types of alerts (hazard:red, contact: green (informational), other: green)
              inputProps={{
                "data-cy": title + "-textfield-input-" + id,
                style: { color: addressAlertColor(address) },
              }}
              onFocus={() => {
                if (!memoizedLocked) {
                  setShowModal(true);
                  !!pauseLocationPolling && pauseLocationPolling();
                }
              }}
              onClick={() => !memoizedLocked && setShowModal(true)}
              onBlur={() => !!resumeLocationPolling && resumeLocationPolling()}
              InputProps={{
                style: { width: `${inputWidth}px` },
              }}
              // className={addressWarning ? classes.warning : null}
            />
          ) : (
            // </FormControl>

            // <FormControl data-cy="narrative-full-width-form-control" fullWidth className={classes.formControl}>
            <Autocomplete
              value={localAddress}
              onBlur={handleBlur}
              autoHighlight
              disabled={memoizedLocked}
              autoSelect
              onKeyDown={handleKeyBlur}
              onInputChange={(event, newValue) => setSearchValue(newValue)}
              onChange={(event, newValue, reason) => {
                if (reason === "blur") {
                  handleBlur();
                  return;
                }
                if (reason === "clear") {
                  handleAddressChange("");
                } else {
                  if (
                    typeof newValue === "object" &&
                    newValue &&
                    newValue?.action !== "Add"
                  ) {
                    getAddress({ variables: { addressId: newValue.id } });
                    setShowModal(false);
                    !!resumeLocationPolling && resumeLocationPolling();
                  }
                  if (
                    newValue &&
                    newValue.action &&
                    newValue.action === "Add"
                  ) {
                    parseInputToAddress(newValue.inputValue);
                    setAddAddressModal(true);
                  }
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options ? filter(options, params) : [];
                // Suggest the creation of a new value
                if (params.inputValue !== "") {
                  filtered.push({
                    inputValue: params.inputValue,
                    value: `Add "${params.inputValue}"`,
                    action: "Add",
                  });
                }

                return filtered;
              }}
              selectOnFocus
              handleHomeEndKeys
              data-cy={`${title}-autocomplete`}
              id="home-address-auto-suggest"
              options={addressOptions}
              getOptionLabel={option => {
                // Value selected with enter, right from the input
                if (typeof option === "string") {
                  return option || "";
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                  return option.inputValue || "";
                }
                if (option.value) {
                  return option.value || "";
                }
                // Autosuggest option
                return option.addressName || "";
              }}
              renderOption={option => option.value}
              style={{ width: `${inputWidth + 40}px` }}
              freeSolo
              onFocus={() => !!pauseLocationPolling && pauseLocationPolling()}
              renderInput={params => (
                // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the replacement input when address editing starts.
                <TextField autoFocus
                  {...params}
                  label={title}
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    "data-cy": `${title}-autocomplete-input`,
                  }}
                />
              )}
            />
          )
          // </FormControl>
        }
        {/* <Fab
          size='small'
          style={{ width: '20px', height: '20px', minHeight: '20px', fontSize: '9px' }}
          onClick={handlePopperClick}
        >
          ?
        </Fab> */}
        <Tooltip title="Click for example addresses">
          <IconButton
            style={{
              ...{
                padding: "0px",
                width: "30px",
                height: "30px",
                fontSize: "20px",
              },
            }}
            onClick={handlePopperClick}
          >
            <MdHelpOutline />
          </IconButton>
        </Tooltip>
      </span>
      <Modal
        // disableEnforceFocus
        onFocus={() => !!pauseLocationPolling && pauseLocationPolling()}
        open={addAddressModal}
        onClose={() => {
          setAddAddressModal(false);
          !!resumeLocationPolling && resumeLocationPolling();
        }}
        className={classes.modal}
      >
        <div className={classes.paper} style={{ display: "flex" }}>
          <TextField
            data-cy={`${title}-new-address-street`}
            id={"new-address-street"}
            label="Street"
            value={`${
              newAddress.streetNumber ? newAddress.streetNumber + " " : ""
            }${newAddress.streetName}`}
            name="street"
            onChange={handleNewAddressChange}
            className={classes.modalInput}
            disabled={memoizedLocked}
          />
          <TextField
            data-cy={`${title}-new-address-unit`}
            id={"new-address-unit"}
            label="Unit"
            value={newAddress.unit}
            name="unit"
            onChange={handleNewAddressChange}
            className={classes.modalInput}
            disabled={memoizedLocked}
          />
          {/* <TextField
            id={"new-address-city"}
            label="City"
            value={newAddress.city}
            name='city'
            onChange={handleNewAddressChange}
            className={classes.modalInput}
            disabled={memoizedLocked}
          /> */}
          <Autocomplete
            id="city-auto-complete"
            freeSolo
            autoSelect={true}
            autoHighlight={false}
            openOnFocus={true}
            groupBy={() => (STATE_CODE || "VT") + " Cities"}
            options={CITY_ARRAY}
            inputValue={newAddress.city || ""}
            onInputChange={(event, newInputValue, reason) => {
              setNewAddress(prevState => {
                return {
                  ...prevState,
                  city: newInputValue,
                };
              });
              if (CITY_ARRAY.includes(newInputValue)) {
                const zips = CITY_ZIP_CODES[newInputValue]?.split(",") || [];
                setNewAddress(prevState => {
                  return {
                    ...prevState,
                    state: STATE_CODE || "VT",
                    zip: zips.length === 1 ? zips[0] : "",
                  };
                });
              }
              if (reason === "clear") {
                setNewAddress(prevState => {
                  return {
                    ...prevState,
                    state: "",
                    zip: "",
                  };
                });
              }
            }}
            style={{ width: "fit-content" }}
            renderInput={params => (
              <TextField
                data-cy={`${title}-new-address-city`}
                style={{ width: 200, margin: "10px 10px" }}
                InputLabelProps={{ shrink: true }}
                {...params}
                label="City"
                margin="normal"
                variant="standard"
              />
            )}
          />
          {/* <TextField
            id={"new-address-state"}
            label="State"
            value={newAddress.state}
            name='state'
            onChange={handleNewAddressChange}
            className={classes.modalInput}
            disabled={memoizedLocked}
          /> */}
          {/* 
          <TextField
            id={"new-address-zip"}
            label="Zip"
            value={newAddress.zip}
            name='zip'
            onChange={handleNewAddressChange}
            className={classes.modalInput}
            disabled={memoizedLocked}
            helperText={CITY_ZIP_CODES[newAddress.city] ? `ZIP Codes for ${newAddress.city}: ${CITY_ZIP_CODES[newAddress.city]}` : null}
          /> */}
          <Autocomplete
            id="state-auto-complete"
            freeSolo
            autoSelect={true}
            autoHighlight={true}
            openOnFocus={true}
            options={statesStrings || []}
            inputValue={newAddress.state || ""}
            onInputChange={(event, newInputValue) => {
              let stateAbb;
              if (statesStrings.some(s => s === newInputValue)) {
                stateAbb = newInputValue;
              } else {
                stateAbb =
                  STATE_TO_ABBREVIATION[newInputValue?.toUpperCase()] ||
                  newInputValue;
              }
              setNewAddress(prevState => {
                return {
                  ...prevState,
                  state: stateAbb,
                };
              });
            }}
            style={{ width: "fit-content" }}
            renderInput={params => (
              <TextField
                data-cy={`${title}-new-address-state`}
                style={{ width: 200, margin: "10px 10px" }}
                InputLabelProps={{ shrink: true }}
                {...params}
                label="State"
                margin="normal"
                variant="standard"
              />
            )}
          />
          <Autocomplete
            id="zip-auto-complete"
            freeSolo
            autoSelect={true}
            autoHighlight={true}
            openOnFocus={true}
            options={CITY_ZIP_CODES[newAddress.city]?.split(",") || []}
            inputValue={newAddress.zip || ""}
            onInputChange={(event, newInputValue) => {
              setNewAddress(prevState => {
                return {
                  ...prevState,
                  zip: newInputValue?.trim(),
                };
              });
            }}
            style={{ width: "fit-content" }}
            renderInput={params => (
              <TextField
                data-cy={`${title}-new-address-zip`}
                style={{ width: 200, margin: "10px 10px" }}
                InputLabelProps={{ shrink: true }}
                {...params}
                label="Zip"
                margin="normal"
                variant="standard"
              />
            )}
          />
          <Button
            data-cy={`${title}-create-address-button`}
            onClick={!memoizedLocked ? () => createNewAddress() : null}
          >
            Create Address
          </Button>
          <Button
            data-cy={`${title}-cancel-create-address-button`}
            onClick={() => setAddAddressModal(false)}
          >
            Cancel
          </Button>
        </div>
      </Modal>
      <Popper
        placement="right"
        open={showPopper}
        disablePortal={true}
        anchorEl={anchorEl}
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: true,
            // boundariesElement: 'scrollParent',
            boundariesElement: "viewport",
            // boundariesElement: 'window',
          },
        }}
        style={{ zIndex: 1400 }}
      >
        <Paper
          style={{
            padding: 10,
            border: "3px solid grey",
            boxShadow: "1px 1px 1px grey",
          }}
        >
          <Typography>
            <CloseIcon
              style={{ float: "right" }}
              onClick={handlePopperClick}
              className={classes.hover}
            />
            <b>Example address formats</b>
            <br />
            <b>Home address: </b>123 S Main St, #4, Waterbury, VT, 05671
            <br />
            <b>Route: </b>4747 VT Route 7, Georgia, VT, 05468
            <br />
            <b>Intersection: </b>Main St / Mill St, Brighton, VT, 05846
            <br />
            <b>Highway Mile Marker: </b>I 89 S MM128, Highgate, VT, 05459
            <br />
          </Typography>
        </Paper>
      </Popper>
    </>
  );
};

export default AddressInputAutoSuggest;
